<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { useDealsStore } from 'dashboard/stores/deals';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  pipelineId: { type: Number, default: null },
});

const { t } = useI18n();
const dealsStore = useDealsStore();
const dialogRef = ref(null);

// Palette pronta per le nuove colonne e per il selettore colore di ognuna.
const STAGE_COLORS = [
  '#64748B',
  '#3B82F6',
  '#A855F7',
  '#10B981',
  '#EF4444',
  '#F59E0B',
  '#EC4899',
  '#14B8A6',
];

const storeStages = computed(() => {
  const pipeline = dealsStore.pipelines.find(p => p.id === props.pipelineId);
  return pipeline?.stages || [];
});

// Copia locale su cui lavora il drag & drop e gli input, riallineata dopo ogni azione.
const localStages = ref([]);
const newStageName = ref('');
const isBusy = ref(false);

const typeOptions = computed(() => [
  { value: 'open', label: t('DEALS.STAGES.TYPES.open') },
  { value: 'won', label: t('DEALS.STAGES.TYPES.won') },
  { value: 'lost', label: t('DEALS.STAGES.TYPES.lost') },
]);

const syncLocal = () => {
  localStages.value = storeStages.value.map(stage => ({ ...stage }));
};

const open = () => {
  syncLocal();
  newStageName.value = '';
  dialogRef.value?.open();
};

const addStage = async () => {
  const name = newStageName.value.trim();
  if (!name || isBusy.value) return;
  isBusy.value = true;
  try {
    await dealsStore.createStage(props.pipelineId, {
      name,
      color: STAGE_COLORS[storeStages.value.length % STAGE_COLORS.length],
      stageType: 'open',
    });
    newStageName.value = '';
    syncLocal();
  } catch (error) {
    useAlert(error.message || t('DEALS.STAGES.API.ERROR'));
  } finally {
    isBusy.value = false;
  }
};

const applyUpdate = async (stage, changes) => {
  try {
    await dealsStore.updateStage(props.pipelineId, stage.id, changes);
    syncLocal();
  } catch (error) {
    useAlert(error.message || t('DEALS.STAGES.API.ERROR'));
    syncLocal();
  }
};

const onRename = stage => {
  const name = (stage.name || '').trim();
  if (!name || name === storeStages.value.find(s => s.id === stage.id)?.name) {
    return;
  }
  applyUpdate(stage, { name });
};

const onColor = (stage, color) => {
  stage.color = color;
  applyUpdate(stage, { color });
};

const onType = (stage, stageType) => {
  stage.stageType = stageType;
  applyUpdate(stage, { stageType });
};

const deleteStage = async stage => {
  if (storeStages.value.length <= 1) {
    useAlert(t('DEALS.STAGES.LAST_STAGE'));
    return;
  }
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('DEALS.STAGES.DELETE_CONFIRM', { name: stage.name }))) {
    return;
  }
  try {
    await dealsStore.deleteStage(props.pipelineId, stage.id);
    syncLocal();
  } catch (error) {
    useAlert(error.message || t('DEALS.STAGES.API.ERROR'));
  }
};

const onReorder = async () => {
  const ids = localStages.value.map(stage => stage.id);
  try {
    await dealsStore.reorderStages(props.pipelineId, ids);
    syncLocal();
  } catch (error) {
    useAlert(error.message || t('DEALS.STAGES.API.ERROR'));
    syncLocal();
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :show-cancel-button="false"
    :confirm-button-label="t('DEALS.STAGES.DONE')"
    @confirm="dialogRef?.close()"
  >
    <div class="flex flex-col gap-4">
      <span class="text-base font-medium text-n-slate-12">
        {{ t('DEALS.STAGES.TITLE') }}
      </span>

      <Draggable
        :list="localStages"
        item-key="id"
        handle=".drag-handle"
        class="flex flex-col gap-2"
        ghost-class="opacity-40"
        @end="onReorder"
      >
        <template #item="{ element: stage }">
          <div class="flex items-center gap-2 p-2 rounded-lg bg-n-alpha-1">
            <Icon
              icon="i-lucide-grip-vertical"
              class="size-4 shrink-0 cursor-grab drag-handle text-n-slate-10"
            />

            <div class="flex items-center gap-1 shrink-0">
              <button
                v-for="color in STAGE_COLORS"
                :key="color"
                type="button"
                class="rounded-full size-4 outline outline-1 outline-offset-1"
                :class="
                  stage.color === color
                    ? 'outline-n-slate-11'
                    : 'outline-transparent'
                "
                :style="{ backgroundColor: color }"
                @click="onColor(stage, color)"
              />
            </div>

            <Input
              v-model="stage.name"
              size="sm"
              class="grow min-w-0"
              @blur="onRename(stage)"
            />

            <Select
              :model-value="stage.stageType"
              :options="typeOptions"
              class="shrink-0"
              @update:model-value="value => onType(stage, value)"
            />

            <Button
              variant="ghost"
              size="sm"
              color="ruby"
              icon="i-lucide-trash-2"
              @click="deleteStage(stage)"
            />
          </div>
        </template>
      </Draggable>

      <div class="flex items-center gap-2">
        <Input
          v-model="newStageName"
          size="sm"
          class="grow"
          :placeholder="t('DEALS.STAGES.ADD_PLACEHOLDER')"
          @keydown.enter.prevent="addStage"
        />
        <Button
          size="sm"
          icon="i-lucide-plus"
          :label="t('DEALS.STAGES.ADD')"
          :is-loading="isBusy"
          :disabled="!newStageName.trim() || isBusy"
          @click="addStage"
        />
      </div>
    </div>
  </Dialog>
</template>
